import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight, X } from 'lucide-react';
import { Input } from './ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gameLabels = {
  yugioh: 'Yu-Gi-Oh!',
  magic: 'Magic',
  pokemon: 'Pokémon',
  accessory: 'Accessoire'
};
const gameColors = {
  yugioh: 'bg-rose-500/80',
  magic: 'bg-blue-500/80',
  pokemon: 'bg-amber-500/80',
  accessory: 'bg-emerald-500/80'
};

export const SearchAutocomplete = ({ className, inputClassName, onNavigate, initialQuery, onQueryChange }) => {
  const [query, setQuery] = useState(initialQuery || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const navigate = useNavigate();
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (query.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/products/search/suggest?q=${encodeURIComponent(query)}`);
        setResults(res.data);
        setOpen(res.data.length > 0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const goToProduct = (productId) => {
    setOpen(false);
    setQuery('');
    navigate(`/shop?highlight=${productId}`);
    onNavigate?.();
  };

  const goToSearch = () => {
    if (!query.trim()) return;
    setOpen(false);
    navigate(`/shop?search=${encodeURIComponent(query)}`);
    setQuery('');
    onNavigate?.();
  };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === 'Enter') {
        e.preventDefault();
        goToSearch();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < results.length) {
        goToProduct(results[selectedIndex].product_id);
      } else {
        goToSearch();
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className || ''}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <Input
          type="text"
          placeholder="Rechercher..."
          value={query}
          onChange={(e) => { 
            setQuery(e.target.value); 
            setSelectedIndex(-1);
            onQueryChange?.(e.target.value);
          }}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          onKeyDown={handleKeyDown}
          className={`pl-10 ${query ? 'pr-8' : ''} bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-fuchsia-500 ${inputClassName || 'w-48'}`}
          data-testid="search-autocomplete-input"
        />
        {query && (
          <button
            type="button"
            onClick={() => { 
              setQuery(''); 
              setResults([]); 
              setOpen(false);
              onQueryChange?.('');
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
            data-testid="search-clear-btn"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        {loading && !query && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-fuchsia-500 border-t-transparent rounded-full animate-spin" />
        )}
      </div>

      {open && (
        <div className="absolute top-full mt-2 w-80 right-0 bg-[#1a1a1a] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-[100]" data-testid="search-suggestions-dropdown">
          <div className="max-h-[400px] overflow-y-auto">
            {results.map((product, i) => (
              <button
                key={product.product_id}
                onClick={() => goToProduct(product.product_id)}
                className={`w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left ${selectedIndex === i ? 'bg-white/10' : ''}`}
                data-testid={`suggestion-${product.product_id}`}
              >
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-10 h-10 rounded-lg object-cover shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{product.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded text-white ${gameColors[product.game] || 'bg-gray-500'}`}>
                      {gameLabels[product.game] || product.game}
                    </span>
                    {product.stock === 0 && <span className="text-[10px] text-red-400">Rupture</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {product.original_price ? (
                    <>
                      <p className="text-fuchsia-400 font-semibold text-sm">{product.price.toFixed(2)} €</p>
                      <p className="text-gray-500 text-xs line-through">{product.original_price.toFixed(2)} €</p>
                    </>
                  ) : (
                    <p className="text-fuchsia-400 font-semibold text-sm">{product.price.toFixed(2)} €</p>
                  )}
                </div>
              </button>
            ))}
          </div>
          <button
            onClick={goToSearch}
            className={`w-full flex items-center justify-center gap-2 p-3 border-t border-white/10 text-fuchsia-400 hover:bg-white/5 transition-colors text-sm ${selectedIndex === results.length ? 'bg-white/10' : ''}`}
            data-testid="search-view-all"
          >
            Voir tous les résultats <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};
